// src/router/AppRouter.tsx
import React, { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';

import MainLayout from '../layout/MainLayout/MainLayout';
import OnboardingLayout from '@layout/OnboardingLayout/OnboardingLayout';
import { FirebaseAnalytics } from '@components/analytics/FirebaseAnalytics';
import { AppLoader } from '@components/ui/AppLoader/AppLoader';
import AuthGuard from './AuthGuard';
import { useAppSelector } from '../store/helpers';

const Login = lazy(() => import('../pages/Login/Login'));
const Dashboard = lazy(() => import('../pages/Dashboard/Dashboard'));
const Projects = lazy(() => import('../pages/Projects/Projects'));
const ProjectDetails = lazy(() => import('../pages/ProjectDetails/ProjectDetails'));
const NotFound = lazy(() => import('../pages/NotFound/NotFound'));
const Organizations = lazy(() => import('@pages/Organizations/Organizations'));
const Bundles = lazy(() => import('@pages/Bundles/Bundles'));
const OrganizationUsersAccess = lazy(
  () => import('@pages/OrganizationUsersAccess/OrganizationUsersAccess')
);
const OrganizationBilling = lazy(() => import('@pages/OrganizationBilling/OrganizationBilling'));
const OrganizationSettings = lazy(() => import('@pages/OrganizationSettings/OrganizationSettings'));
const ProjectMembers = lazy(() => import('@pages/ProjectMembers/ProjectMembers'));
const ProjectActivity = lazy(() => import('@pages/ProjectActivity/ProjectActivity'));
const ProjectSettings = lazy(() => import('@pages/ProjectSettings/ProjectSettings'));
const ProjectUserProperties = lazy(
  () => import('@pages/ProjectUserProperties/ProjectUserProperties')
);
const OrgInviteAccept = lazy(() => import('@pages/OrgInviteAccept/OrgInviteAccept'));
const ProjectInviteAccept = lazy(() => import('@pages/ProjectInviteAccept/ProjectInviteAccept'));
const Register = lazy(() => import('@pages/Register/Register'));
const Activation = lazy(() => import('@pages/Activation/Activation'));
const PasswordResetRequest = lazy(() => import('@pages/PasswordResetRequest/PasswordResetRequest'));
const PasswordResetConfirm = lazy(() => import('@pages/PasswordResetConfirm/PasswordResetConfirm'));
const UserSettings = lazy(() => import('@pages/UserSettings/UserSettings'));
const PatManagement = lazy(() => import('@pages/PatManagement/PatManagement'));
const OAuthCallback = lazy(() => import('@pages/OAuthCallback/OAuthCallback'));
const Notifications = lazy(() => import('@pages/Notifications/Notifications'));
const CliAuthorize = lazy(() => import('@pages/CliAuthorize/CliAuthorize'));
const PaddleCheckout = lazy(() => import('@pages/PaddleCheckout/PaddleCheckout'));

const lazyPage = (page: React.ReactElement) => (
  <Suspense fallback={<AppLoader size="large" minHeight={160} />}>{page}</Suspense>
);
const onboardingPage = (page: React.ReactElement) =>
  lazyPage(<OnboardingLayout>{page}</OnboardingLayout>);

const resolveAuthenticatedRedirect = (search: string) => {
  const params = new URLSearchParams(search);
  const returnTo = params.get('returnTo')?.trim() || '';

  if (!returnTo) {
    return '/organizations';
  }
  if (!returnTo.startsWith('/') || returnTo.startsWith('//')) {
    return '/organizations';
  }
  return returnTo;
};

const PublicAuthRedirect = () => {
  const location = useLocation();
  const target = resolveAuthenticatedRedirect(location.search);
  return <Navigate to={target} replace />;
};

const AppRouter = () => {
  const { token, user } = useAppSelector(state => state.auth);
  const activationStatus = user?.isActivated ?? (user as { activated?: boolean } | null)?.activated;
  const isActivated = activationStatus !== false;
  return (
    <BrowserRouter unstable_useTransitions={false}>
      <FirebaseAnalytics />
      <Routes>
        {/* Public route */}
        <Route
          path="/login"
          element={
            token ? (
              isActivated ? (
                <PublicAuthRedirect />
              ) : (
                <Navigate to="/activation" replace />
              )
            ) : (
              onboardingPage(<Login />)
            )
          }
        />
        <Route
          path="/register"
          element={
            token ? (
              isActivated ? (
                <PublicAuthRedirect />
              ) : (
                <Navigate to="/activation" replace />
              )
            ) : (
              onboardingPage(<Register />)
            )
          }
        />
        <Route
          path="/activation"
          element={token && isActivated ? <PublicAuthRedirect /> : onboardingPage(<Activation />)}
        />
        <Route
          path="/password-reset"
          element={
            token && isActivated ? <PublicAuthRedirect /> : onboardingPage(<PasswordResetRequest />)
          }
        />
        <Route
          path="/password-reset/confirm"
          element={
            token && isActivated ? <PublicAuthRedirect /> : onboardingPage(<PasswordResetConfirm />)
          }
        />
        <Route path="/auth/github/callback" element={lazyPage(<OAuthCallback />)} />
        <Route path="/auth/gitlab/callback" element={lazyPage(<OAuthCallback />)} />
        <Route path="/auth/microsoft/callback" element={lazyPage(<OAuthCallback />)} />
        <Route path="/cli/authorize" element={lazyPage(<CliAuthorize />)} />
        <Route path="/paddle-checkout" element={lazyPage(<PaddleCheckout />)} />
        <Route path="/org-invites/accept" element={onboardingPage(<OrgInviteAccept />)} />
        <Route path="/project-invites/accept" element={onboardingPage(<ProjectInviteAccept />)} />
        {/* Protected routes */}
        <Route element={<AuthGuard />}>
          <Route element={<MainLayout />}>
            <Route path="/" element={<Navigate to="/organizations" />} />
            <Route path="/dashboard" element={lazyPage(<Dashboard />)} />
            <Route path="/organizations" element={lazyPage(<Organizations />)} />
            <Route
              path="/organizations/:orgSlug/users"
              element={lazyPage(<OrganizationUsersAccess />)}
            />
            <Route
              path="/organizations/:orgSlug/billing"
              element={lazyPage(<OrganizationBilling />)}
            />
            <Route
              path="/organizations/:orgSlug/settings"
              element={lazyPage(<OrganizationSettings />)}
            />
            <Route path="/organizations/:orgSlug/projects" element={lazyPage(<Projects />)} />
            <Route
              path="/organizations/:orgSlug/projects/:projectSlug"
              element={lazyPage(<ProjectDetails />)}
            />
            <Route
              path="/organizations/:orgSlug/projects/:projectSlug/dashboard"
              element={lazyPage(<Dashboard />)}
            />
            <Route
              path="/organizations/:orgSlug/projects/:projectSlug/members"
              element={lazyPage(<ProjectMembers />)}
            />
            <Route
              path="/organizations/:orgSlug/projects/:projectSlug/history"
              element={lazyPage(<ProjectActivity />)}
            />
            <Route
              path="/organizations/:orgSlug/projects/:projectSlug/settings"
              element={lazyPage(<ProjectSettings />)}
            />
            <Route
              path="/organizations/:orgSlug/projects/:projectSlug/user-properties"
              element={lazyPage(<ProjectUserProperties />)}
            />
            <Route
              path="/organizations/:orgSlug/projects/:projectSlug/channels/:channelId/bundles"
              element={lazyPage(<Bundles />)}
            />
            <Route path="/projects" element={lazyPage(<Projects />)} />
            <Route path="/projects/:slug" element={lazyPage(<ProjectDetails />)} />
            <Route path="/projects/:projectSlug/dashboard" element={lazyPage(<Dashboard />)} />
            <Route path="/projects/:projectSlug/members" element={lazyPage(<ProjectMembers />)} />
            <Route path="/projects/:projectSlug/history" element={lazyPage(<ProjectActivity />)} />
            <Route
              path="/projects/:projectSlug/user-properties"
              element={lazyPage(<ProjectUserProperties />)}
            />
            <Route path="/projects/:projectSlug/settings" element={lazyPage(<ProjectSettings />)} />
            <Route
              path="/projects/:projectSlug/channels/:channelId/bundles"
              element={lazyPage(<Bundles />)}
            />
            <Route path="/user/settings" element={lazyPage(<UserSettings />)} />
            <Route path="/user/notifications" element={lazyPage(<Notifications />)} />
            <Route path="/user/tokens" element={lazyPage(<PatManagement />)} />
          </Route>
        </Route>

        {/* 404 fallback */}
        <Route path="*" element={lazyPage(<NotFound />)} />
      </Routes>
    </BrowserRouter>
  );
};

export default AppRouter;
